import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { getResourcePath, isGulpOrBranching } from 'owa-config';
import {
    defaultCreateHTML,
    defaultCreateScript,
    defaultCreateScriptUrl,
} from './defaultTrustedTypesPolicy';
import { isBootFeatureEnabled } from 'owa-metatags';
import { trace } from 'owa-trace';
import type { TraceErrorObject } from 'owa-analytics-types';
import { logStartGreyError } from 'owa-analytics-start';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';

let owaTrustedTypesPolicy: Pick<TrustedTypePolicy, 'createHTML' | 'createScriptURL'> | undefined;
// Regex to match either /mail/sw.js or /mail/<any string>/sw.js or /mail/<any string>/<any string>/sw.js
const regex: RegExp = /^\/mail\/([^\/]+\/)*sw\.js/;
// Regex to match either /<any string>/<any string>/sw_webpush.js or /sw_<any string>_webpush.js?branch=<any string>
// Includes OWA mini `{prefix}/sw_mini_webpush.js` as defined by router in Microsoft.Owa.Web.Server/Startup.cs
const webpushRegex: RegExp =
    /^\/([^\/]+\/)*sw(_[^\/_]+)*_webpush\.js(\?(branch|ring|version)=[^\/_=]+){0,1}$/;

const isValidUrl = (url: string): boolean => {
    if (
        url === `${getResourcePath()}mos-proxy.js` ||
        url === 'https://content.lifecycle.office.net/dime/v1/dime.js'
    ) {
        return true;
    }
    return false;
};

if (window.trustedTypes) {
    owaTrustedTypesPolicy = window.trustedTypes.createPolicy('owaTrustedTypesPolicy', {
        createHTML: (html: string, extendedConfig: Config): string => {
            if (html === '') {
                return '';
            }
            const trustedHTML = DOMPurify.sanitize(html, {
                ...extendedConfig,
                RETURN_TRUSTED_TYPE: false,
            });

            return trustedHTML?.toString() ?? '';
        },
        createScriptURL: (url: string): string => {
            if (isValidUrl(url) || regex.test(url) || webpushRegex.test(url)) {
                return url;
            }

            // The URL is not meeting the conditions for a safe URL so return empty string
            return '';
        },
    });

    if (isBootFeatureEnabled('fwk-warn-duplicate-default-tt-policy')) {
        // This is to check if a default policy was already created by an external source before us
        const isDefaultPolicyCreatedByExternalSourceFirst = !!window.trustedTypes.defaultPolicy;

        // Save a reference to the original createPolicy method
        const originalCreatePolicy = window.trustedTypes.createPolicy;

        // Override the createPolicy method with proper TypeScript typing
        window.trustedTypes.createPolicy = function <Options extends TrustedTypePolicyOptions>(
            policyName: string,
            policyOptions?: Options
        ) {
            // Check if this is an attempt to create a default policy when one already exists
            if (
                policyName === 'default' &&
                window.trustedTypes &&
                window.trustedTypes.defaultPolicy
            ) {
                const error: TraceErrorObject = new Error(
                    'Duplicated Default Trusted Types policy'
                );
                trace.warn(`${error.message}: ${error.stack}`);

                if (isDefaultPolicyCreatedByExternalSourceFirst) {
                    // We should throw the error to avoid using a pre-existing default policy from an external source.
                    error.external = true;

                    // Using getGlobalSettingsAccountMailboxInfo: This is infrastructure telemetry about
                    // CSP trusted types policy conflicts. It tracks when external sources create policies
                    // before OWA and is not tied to any specific user data.
                    logStartGreyError(
                        'ExternalDefaultTrustedTypesPolicyCreatedFirst',
                        getGlobalSettingsAccountMailboxInfo(),
                        error
                    );
                    throw error;
                }

                // There is no need to throw the error if we created the default policy first, it can't be overwritten by users or extensions.

                // Using getGlobalSettingsAccountMailboxInfo: This is infrastructure telemetry about
                // CSP trusted types policy management. It tracks when OWA's policy is already active
                // and is not tied to any specific user data.
                logStartGreyError(
                    'OwaDefaultTrustedTypesPolicyCreatedFirst',
                    getGlobalSettingsAccountMailboxInfo(),
                    error
                );

                // Return the existing default policy
                return window.trustedTypes.defaultPolicy;
            }

            // For all other cases, use the original implementation
            return originalCreatePolicy.call(window.trustedTypes, policyName, policyOptions);
        };
    }

    /**
     * The Trusted Types default policy will only be define on prod builds, on dev mode and PR branches we want to
     * keep throwing the errors from the CSP so that we fix them before merging to master.
     * */
    if (!isGulpOrBranching() && isBootFeatureEnabled('fwk-enable-default-trusted-types-policy')) {
        const isTrustedTypesEnabled = isBootFeatureEnabled('fwk-enforce-trusted-types');

        /**
         * When there is no default trusted types policy defined, execution of code related to an untrusted input will be blocked
         * by the CSP when it is in enforce mode and an error will be thrown, but when there is a default trusted types policy
         * defined, since it catches all unhandled trusted type errors, code execution will not be blocked by CSP even when it
         * is in enforce mode and the error will not be thrown, so to keep the same behavior and block/sanitize code related to untrusted
         * inputs we have to handle each case manually on these functions.
         */
        window.trustedTypes.createPolicy('default', {
            createHTML: (html: string, type: string, sink: string) =>
                defaultCreateHTML(html, type, sink, isTrustedTypesEnabled),
            createScriptURL: (url: string, type: string, sink: string) =>
                defaultCreateScriptUrl(url, type, sink, isTrustedTypesEnabled),
            createScript: (script: string, type: string, sink: string) =>
                defaultCreateScript(script, type, sink, isTrustedTypesEnabled),
        });
    }
}

export { owaTrustedTypesPolicy, regex, webpushRegex };
